// The scene editor body: name + tile colour, then a dense grid of device tiles
// (gangs/outputs grouped under each device name). Tap chips to choose each
// device's target state. Shared by the standalone Scenes screen and the inline
// scenes strip (via RoomScenesManager).

import React, { useMemo } from 'react';
import {
  Box, Typography, Grid, Chip, TextField, Button, Slider, Checkbox, FormControlLabel,
} from '@mui/material';

// Colour each light channel's slider so it reads at a glance (mirrors the main
// light page / LightControlCard).
function channelColor(ch) {
  const c = String(ch).toLowerCase();
  if (c === 'r' || c.startsWith('red')) return '#e53935';
  if (c === 'g' || c.startsWith('green')) return '#43a047';
  if (c === 'b' || c.startsWith('blue')) return '#1e88e5';
  if (c === 'w' || c === 'white') return '#e0e0e0';
  if (c === 'ww' || c === 'wh' || c.startsWith('warm')) return '#ffb300';
  return '#90caf9';
}

// One light control rendered as 0-255 sliders per channel — the same set-up as
// the device page, but here the values are stored into the scene. A checkbox
// includes/excludes the light from the scene (unchecked = leave it untouched).
function LightControl({ control, value, onSetValue }) {
  const active = value && value !== 'ignore';
  const vals = control.channels.map((_, i) => {
    if (!active) return 0;
    const n = Number(String(value).split(',')[i]);
    return Number.isNaN(n) ? 0 : Math.max(0, Math.min(255, n));
  });
  const commit = (arr) => onSetValue(control.id, arr.join(','));
  const setChan = (i, v) => { const u = [...vals]; u[i] = v; commit(u); };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <FormControlLabel
          sx={{ m: 0 }}
          control={(
            <Checkbox
              size="small" checked={!!active}
              onChange={(e) => onSetValue(control.id, e.target.checked ? vals.join(',') : 'ignore')}
            />
          )}
          label={<Typography variant="caption" color="text.secondary">Set in scene</Typography>}
        />
        {active && (
          <Button size="small" onClick={() => commit(control.channels.map(() => 0))} sx={{ minWidth: 0, px: 1 }}>
            Off
          </Button>
        )}
      </Box>
      <Box sx={{
        display: 'flex', gap: 1, justifyContent: 'space-around', alignItems: 'flex-end',
        pt: 0.5, opacity: active ? 1 : 0.45,
      }}>
        {control.channels.map((ch, i) => (
          <Box key={ch} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
            <Typography variant="caption" color="text.primary" sx={{ lineHeight: 1, minHeight: 14 }}>{vals[i]}</Typography>
            <Slider
              orientation="vertical"
              min={0} max={255} value={vals[i]}
              onChange={(_, v) => setChan(i, v)}
              sx={{
                height: 110, color: channelColor(ch),
                '& .MuiSlider-rail': { opacity: 0.25 },
                '& .MuiSlider-thumb': { width: 14, height: 14 },
              }}
            />
            <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ lineHeight: 1 }}>{ch}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function SceneEditor({ draft, controls, busy, onName, onColor, onToggle, onSetValue, onSave, onCancel }) {
  const selected = draft.selections;
  const setCount = useMemo(
    () => Object.values(selected).filter((v) => v && v !== 'ignore').length,
    [selected],
  );
  // Group controls by device so gangs/outputs sit under one device-name header.
  const groups = useMemo(() => {
    const m = new Map();
    controls.forEach((c) => {
      if (!m.has(c.uniqueNumber)) m.set(c.uniqueNumber, { uno: c.uniqueNumber, name: c.deviceName, controls: [] });
      m.get(c.uniqueNumber).controls.push(c);
    });
    return [...m.values()];
  }, [controls]);

  return (
    <Box sx={{ p: 1.5, maxWidth: 1100, mx: 'auto' }}>
      {/* Compact header: name + color + actions all on one always-visible row. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
        <TextField
          size="small" label="Scene name" value={draft.name} onChange={(e) => onName(e.target.value)}
          autoFocus placeholder="e.g. Movie Time" sx={{ flex: '1 1 220px', minWidth: 180 }}
        />
        <Box
          component="label"
          title="Tile color"
          sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: draft.color, border: '2px solid', borderColor: 'divider', cursor: 'pointer', flexShrink: 0 }}
        >
          <input type="color" value={draft.color} onChange={(e) => onColor(e.target.value)}
            style={{ opacity: 0, width: 0, height: 0 }} />
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button size="small" variant="contained" onClick={onSave} disabled={busy}>
          {busy ? 'Saving…' : `Save · ${setCount}`}
        </Button>
      </Box>

      {/* Dense grid of device tiles (one per device, gangs/outputs grouped under
          the device name), many per row, tap chips to choose. */}
      <Grid container spacing={1}>
        {groups.map((g) => {
          const active = g.controls.some((c) => selected[c.id] && selected[c.id] !== 'ignore');
          // Light devices show channel sliders — give the 5-channel ones room.
          const light = g.controls.find((c) => c.kind === 'light');
          const wide = light && light.channels.length >= 5;
          return (
            <Grid item xs={wide ? 12 : 6} sm={wide ? 6 : 4} md={wide ? 5 : 3} key={g.uno}>
              <Box sx={{
                border: '1px solid', borderColor: active ? 'primary.main' : 'divider',
                borderRadius: 2, p: 1, height: '100%',
              }}>
                <Typography variant="caption" fontWeight={700} noWrap sx={{ display: 'block', mb: 0.5 }}>
                  {g.name}
                </Typography>
                {g.controls.map((c) => {
                  if (c.kind === 'light') {
                    return (
                      <LightControl key={c.id} control={c} value={selected[c.id]} onSetValue={onSetValue} />
                    );
                  }
                  const showLabel = c.label !== g.name; // multi-gang / multi-output
                  return (
                    <Box key={c.id} sx={{ mb: 0.5 }}>
                      {showLabel && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {c.label}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {c.options.map((o) => (
                          <Chip
                            key={o.key} label={o.label} size="small"
                            color={selected[c.id] === o.key ? 'primary' : 'default'}
                            variant={selected[c.id] === o.key ? 'filled' : 'outlined'}
                            onClick={() => onToggle(c.id, o.key)}
                            sx={{ height: 24, '& .MuiChip-label': { px: 1, fontSize: 12 } }}
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
